import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { loginPathFor } from './ProtectedRoute';

/**
 * #3248 PR 2 — the route guard for pages a magic-link action session may reach.
 *
 * Admits when:
 *   - a full login is stored (the page then works as for any signed-in user), or
 *   - GET /auth/action-session says the HttpOnly bp_action_session cookie (which
 *     page script can't read) is for THIS route's actionType and THIS target:
 *     the session's target kind must match and its id must equal the route
 *     param named by `targetParam` (case-insensitively, as the API compares).
 * Otherwise → /login?redirect=<path + search> (the deep link is kept when safe).
 *
 * The API stays the authority: the allowlist in actionSessionAllowlist.ts
 * refuses anything this guard lets through by mistake. The guard exists so a
 * page doesn't render for a session the API will refuse.
 *
 * No page uses it yet (APPROVE's target page lands with #3187); ProtectedRoute
 * is unchanged.
 */

export interface ActionRouteProps {
  /** The action this route serves, e.g. 'APPROVE'. */
  actionType: string;
  /** The route param holding the target id, e.g. 'artefactId'. */
  targetParam: string;
  /** The target's kind (only 'artefact' exists today). */
  targetKind?: 'artefact';
  children: ReactNode;
}

interface ActionSessionProbe {
  actionType?: unknown;
  target?: { kind?: unknown; id?: unknown } | null;
}

export function probeMatches(
  probe: ActionSessionProbe | null | undefined,
  expected: { actionType: string; targetKind: string; targetId: string | undefined },
): boolean {
  if (!probe || probe.actionType !== expected.actionType) return false;
  const t = probe.target;
  if (!t || t.kind !== expected.targetKind) return false;
  if (typeof t.id !== 'string' || !expected.targetId) return false;
  return t.id.toLowerCase() === expected.targetId.toLowerCase();
}

/**
 * Whether a probe decision may be used for the route now showing: only one
 * made for exactly this key. The render-phase reset clears a stale decision
 * when the key changes, but not one written after that reset: a probe for the
 * previous route answering before its effect's cleanup sets `cancelled`. This
 * check is what refuses that one.
 */
export function decisionApplies<D extends { key: string }>(decision: D | null | undefined, currentKey: string): decision is D {
  return decision != null && decision.key === currentKey;
}

export default function ActionRoute({ actionType, targetParam, targetKind = 'artefact', children }: ActionRouteProps) {
  const location = useLocation();
  const params = useParams();
  const targetId = params[targetParam];
  const hasFullLogin = localStorage.getItem('blueprint_token') !== null;

  // The decision is keyed to the exact inputs it was made for (every route
  // param, not only the target), and forgotten the moment they change: a change
  // of target reads as "in flight" until THIS route is re-probed, so going
  // A → B → A never re-shows A's old admission while A's new probe is pending
  // (Codex R2 F3). A late answer for an earlier route is dropped by the
  // effect's `cancelled` flag and could not match the key anyway.
  const key = JSON.stringify([actionType, targetKind, targetParam, Object.entries(params).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))]);
  const [decision, setDecision] = useState<{ key: string; admitted: boolean } | null>(null);
  const [decidedFor, setDecidedFor] = useState(key);
  if (decidedFor !== key) {
    setDecidedFor(key);
    setDecision(null);
  }
  const admitted = decidedFor === key && decisionApplies(decision, key) ? decision.admitted : undefined;

  useEffect(() => {
    if (hasFullLogin) return;
    let cancelled = false;
    apiFetch<ActionSessionProbe>('/auth/action-session')
      .then((probe) => {
        if (!cancelled) setDecision({ key, admitted: probeMatches(probe, { actionType, targetKind, targetId }) });
      })
      .catch(() => { if (!cancelled) setDecision({ key, admitted: false }); });
    return () => { cancelled = true; };
  }, [hasFullLogin, key, actionType, targetKind, targetId]);

  if (hasFullLogin) return <>{children}</>;
  if (admitted === undefined) return null;
  if (!admitted) return <Navigate to={loginPathFor(location.pathname + location.search)} replace />;
  return <>{children}</>;
}
